import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  FileText, Upload, Trash2, Download, Loader2, CheckCircle2, AlertTriangle,
  Search as SearchIcon, MessageSquare, RefreshCw,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useOrg } from "@/context/OrgContext";
import { PageHeader, Scroller, Spinner, EmptyState, goldBtn } from "@/pages/_shared";

const STATUS = {
  processing: { icon: Loader2, cls: "text-amber-400 animate-spin", label: "Processing" },
  ready: { icon: CheckCircle2, cls: "text-emerald-400", label: "Ready" },
  failed: { icon: AlertTriangle, cls: "text-red-400", label: "Failed" },
};

export default function Files() {
  const [files, setFiles] = useState(null);
  const [q, setQ] = useState("");
  const fileRef = useRef(null);
  const nav = useNavigate();
  const { activeCompanyId, activeCompany } = useOrg();

  const load = useCallback(async () => {
    const { data } = await api.get("/files", { params: q ? { q } : {} });
    setFiles(data);
  }, [q]);
  useEffect(() => { load(); }, [load, activeCompanyId]);

  useEffect(() => {
    if (!files) return;
    if (files.some((f) => f.status === "processing")) {
      const t = setTimeout(load, 2500);
      return () => clearTimeout(t);
    }
  }, [files, load]);

  const upload = async (list) => {
    for (const f of Array.from(list || [])) {
      try {
        const fd = new FormData();
        fd.append("file", f);
        await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        toast.success(`${f.name} uploaded`);
      } catch (e) { toast.error(`Failed: ${f.name}`); }
    }
    load();
  };

  const del = async (id) => { await api.delete(`/files/${id}`); toast.success("Deleted"); load(); };
  const download = async (f) => {
    try {
      const res = await api.get(`/files/${f.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = f.filename; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Download failed"); }
  };
  const ask = (f) => nav("/app/chat/new", { state: { prompt: `Analyse the document "${f.filename}" I uploaded and give me the key points.`, category: "smart" } });

  if (!files) return <Spinner />;

  return (
    <Scroller>
      <PageHeader title="Documents" subtitle={activeCompany ? `Documents for ${activeCompany.name} — BDVG AI reads and understands them` : "All group documents — BDVG AI reads and understands them"} icon={FileText}
        action={<button onClick={() => fileRef.current?.click()} className={goldBtn} data-testid="files-upload-btn"><Upload className="h-4 w-4" /> Upload</button>} />
      <input ref={fileRef} type="file" multiple hidden onChange={(e) => upload(e.target.files)} data-testid="files-input" />

      <div onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); upload(e.dataTransfer.files); }}
        className="mb-5 flex items-center gap-3 rounded-xl border border-white/10 bg-graphite px-4 py-2.5">
        <SearchIcon className="h-4 w-4 text-white/30" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search files…" data-testid="files-search"
          className="w-full bg-transparent text-sm text-white placeholder:text-white/30 focus:outline-none" />
      </div>

      {files.length === 0 ? (
        <EmptyState icon={Upload} title="No files yet" hint="Upload PDFs, DOCX, spreadsheets, or images. BDVG AI extracts and indexes their content for you.">
          <button onClick={() => fileRef.current?.click()} className={goldBtn}><Upload className="h-4 w-4" /> Upload a file</button>
        </EmptyState>
      ) : (
        <div className="grid gap-2.5 sm:grid-cols-2">
          {files.map((f) => {
            const st = STATUS[f.status] || STATUS.processing;
            return (
              <div key={f.id} className="group rounded-xl border border-white/8 bg-charcoal p-4 transition-colors hover:border-gold/25" data-testid={`file-${f.id}`}>
                <div className="flex items-start justify-between">
                  <div className="flex min-w-0 items-center gap-3">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-gold/10"><FileText className="h-5 w-5 text-gold" /></div>
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium text-white/90">{f.filename}</p>
                      <p className="text-[11px] text-white/35">{(f.size / 1024).toFixed(0)} KB{f.chunk_count ? ` · ${f.chunk_count} chunks` : ""}</p>
                    </div>
                  </div>
                  <div className={`flex items-center gap-1 text-[11px] ${st.cls}`} data-testid={`file-status-${f.id}`}>
                    <st.icon className="h-3.5 w-3.5" /> {st.label}
                  </div>
                </div>
                <div className="mt-3 flex items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100">
                  <button onClick={() => ask(f)} disabled={f.status !== "ready"} className="flex items-center gap-1.5 rounded-lg border border-white/10 px-2.5 py-1 text-xs text-white/70 hover:border-gold/30 hover:text-white disabled:opacity-30" data-testid={`file-ask-${f.id}`}>
                    <MessageSquare className="h-3.5 w-3.5" /> Ask BDVG AI
                  </button>
                  <button onClick={() => download(f)} className="rounded-lg p-1.5 text-white/40 hover:text-white"><Download className="h-4 w-4" /></button>
                  <button onClick={() => del(f.id)} className="rounded-lg p-1.5 text-white/40 hover:text-destructive"><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Scroller>
  );
}
